import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import axios from "axios";

const rateTypes = [
  { id: "margin", name: "Margin" },
  { id: "sales", name: "Sales" },
];

const emptyRate = {
  id: null,
  name: "",
  rate: "",
  rateType: "margin",
};

const emptyRange = {
  id: null,
  min: "",
  max: "",
  multiplier: "",
};

const emptyClientRate = {
  id: null,
  label: "",
  multiplier: "",
};

export default function CommissionsTab({ instanceUniqueId }) {
  const [tab, setTab] = useState(0);
  const [rates, setRates] = useState([]);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [rateDraft, setRateDraft] = useState(emptyRate);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [exclusions, setExclusions] = useState({
    includeFreightCommission: true,
    includeFulfillmentCommission: true,
    includeShippingCommission: true,
  });
  const [maxCommissionPercent, setMaxCommissionPercent] = useState("");
  const [marginRows, setMarginRows] = useState([]);
  const [marginOpen, setMarginOpen] = useState(false);
  const [marginDraft, setMarginDraft] = useState(emptyRange);
  const [clientRates, setClientRates] = useState([]);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientDraft, setClientDraft] = useState(emptyClientRate);
  const [goalRows, setGoalRows] = useState([]);
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalDraft, setGoalDraft] = useState(emptyRange);
  const [instanceId, setInstanceId] = useState(null);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [savingExclusions, setSavingExclusions] = useState(false);
  const [savingMaximum, setSavingMaximum] = useState(false);
  const [savingMargin, setSavingMargin] = useState(false);
  const [savingClientRates, setSavingClientRates] = useState(false);
  const [savingGoals, setSavingGoals] = useState(false);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchRates = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/commission-rates`,
      headers
    );
    setRates(res.data.rates || []);
  };

  const fetchUsers = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(`${SERVER}/auth/byinstance/${targetInstanceId}`, headers);
    setUsers(res.data?.listOfUsers || []);
  };

  const fetchAssignments = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/commission-assignments`,
      headers
    );
    const map = {};
    (res.data.assignments || []).forEach((row) => {
      map[row.userId] = {
        clientRepRateId: row.clientRepRateId ?? "",
        orderRepRateId: row.orderRepRateId ?? "",
        clientOrderRepRateId: row.clientOrderRepRateId ?? "",
      };
    });
    setAssignments(map);
  };

  const fetchExclusions = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/commission-exclusions`,
      headers
    );
    const settings = res.data.settings || {};
    setExclusions({
      includeFreightCommission: settings.includeFreightCommission ?? true,
      includeFulfillmentCommission: settings.includeFulfillmentCommission ?? true,
      includeShippingCommission: settings.includeShippingCommission ?? true,
    });
  };

  const fetchMaximum = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/commission-maximum`,
      headers
    );
    const settings = res.data.settings || {};
    setMaxCommissionPercent(settings.maxCommissionPercent ?? "");
  };

  const fetchMarginTable = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/commission-margin-table`,
      headers
    );
    setMarginRows(res.data.rows || []);
  };

  const fetchClientRates = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/commission-client-rates`,
      headers
    );
    setClientRates(res.data.rows || []);
  };

  const fetchSalesGoals = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/commission-sales-goals`,
      headers
    );
    setGoalRows(res.data.rows || []);
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) {
      fetchRates(instanceId);
      fetchUsers(instanceId);
      fetchAssignments(instanceId);
      fetchExclusions(instanceId);
      fetchMaximum(instanceId);
      fetchMarginTable(instanceId);
      fetchClientRates(instanceId);
      fetchSalesGoals(instanceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreateRate = () => {
    setRateDraft(emptyRate);
    setRatesOpen(true);
  };

  const openEditRate = (rate) => {
    setRateDraft({
      id: rate.id,
      name: rate.name || "",
      rate: rate.rate ?? "",
      rateType: rate.rateType || "margin",
    });
    setRatesOpen(true);
  };

  const saveRate = async () => {
    const payload = {
      name: rateDraft.name.trim(),
      rate: Number(rateDraft.rate),
      rateType: rateDraft.rateType,
    };
    if (!payload.name || !Number.isFinite(payload.rate) || !instanceId) return;

    if (rateDraft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/commission-rates/${rateDraft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(
        `${SERVER}/instances/${instanceId}/commission-rates`,
        payload,
        headers
      );
    }

    setRatesOpen(false);
    fetchRates(instanceId);
  };

  const deleteRate = async (rate) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/commission-rates/${rate.id}`,
      headers
    );
    fetchRates(instanceId);
  };

  const openCreateMargin = () => {
    setMarginDraft(emptyRange);
    setMarginOpen(true);
  };

  const openEditMargin = (row) => {
    setMarginDraft({
      id: row.id,
      min: row.minMargin ?? "",
      max: row.maxMargin ?? "",
      multiplier: row.multiplier ?? "",
    });
    setMarginOpen(true);
  };

  const saveMargin = async () => {
    if (!instanceId) return;
    const payload = {
      minMargin: Number(marginDraft.min),
      maxMargin: marginDraft.max === "" ? null : Number(marginDraft.max),
      multiplier: Number(marginDraft.multiplier),
    };
    if (!Number.isFinite(payload.minMargin) || !Number.isFinite(payload.multiplier)) return;
    setSavingMargin(true);
    try {
      if (marginDraft.id) {
        await axios.put(
          `${SERVER}/instances/${instanceId}/commission-margin-table/${marginDraft.id}`,
          payload,
          headers
        );
      } else {
        await axios.post(
          `${SERVER}/instances/${instanceId}/commission-margin-table`,
          payload,
          headers
        );
      }
      setMarginOpen(false);
      fetchMarginTable(instanceId);
    } finally {
      setSavingMargin(false);
    }
  };

  const deleteMargin = async (row) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/commission-margin-table/${row.id}`,
      headers
    );
    fetchMarginTable(instanceId);
  };

  const openCreateClientRate = () => {
    setClientDraft(emptyClientRate);
    setClientOpen(true);
  };

  const openEditClientRate = (row) => {
    setClientDraft({
      id: row.id,
      label: row.label || "",
      multiplier: row.multiplier ?? "",
    });
    setClientOpen(true);
  };

  const saveClientRate = async () => {
    if (!instanceId) return;
    const payload = {
      label: clientDraft.label.trim(),
      multiplier: Number(clientDraft.multiplier),
    };
    if (!payload.label || !Number.isFinite(payload.multiplier)) return;
    setSavingClientRates(true);
    try {
      if (clientDraft.id) {
        await axios.put(
          `${SERVER}/instances/${instanceId}/commission-client-rates/${clientDraft.id}`,
          payload,
          headers
        );
      } else {
        await axios.post(
          `${SERVER}/instances/${instanceId}/commission-client-rates`,
          payload,
          headers
        );
      }
      setClientOpen(false);
      fetchClientRates(instanceId);
    } finally {
      setSavingClientRates(false);
    }
  };

  const deleteClientRate = async (row) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/commission-client-rates/${row.id}`,
      headers
    );
    fetchClientRates(instanceId);
  };

  const openCreateGoal = () => {
    setGoalDraft(emptyRange);
    setGoalOpen(true);
  };

  const openEditGoal = (row) => {
    setGoalDraft({
      id: row.id,
      min: row.minPercent ?? "",
      max: row.maxPercent ?? "",
      multiplier: row.multiplier ?? "",
    });
    setGoalOpen(true);
  };

  const saveGoal = async () => {
    if (!instanceId) return;
    const payload = {
      minPercent: Number(goalDraft.min),
      maxPercent: goalDraft.max === "" ? null : Number(goalDraft.max),
      multiplier: Number(goalDraft.multiplier),
    };
    if (!Number.isFinite(payload.minPercent) || !Number.isFinite(payload.multiplier)) return;
    setSavingGoals(true);
    try {
      if (goalDraft.id) {
        await axios.put(
          `${SERVER}/instances/${instanceId}/commission-sales-goals/${goalDraft.id}`,
          payload,
          headers
        );
      } else {
        await axios.post(
          `${SERVER}/instances/${instanceId}/commission-sales-goals`,
          payload,
          headers
        );
      }
      setGoalOpen(false);
      fetchSalesGoals(instanceId);
    } finally {
      setSavingGoals(false);
    }
  };

  const deleteGoal = async (row) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/commission-sales-goals/${row.id}`,
      headers
    );
    fetchSalesGoals(instanceId);
  };

  const updateAssignment = (userId, field, value) => {
    setAssignments((prev) => ({
      ...prev,
      [userId]: {
        clientRepRateId: "",
        orderRepRateId: "",
        clientOrderRepRateId: "",
        ...(prev[userId] || {}),
        [field]: value,
      },
    }));
  };

  const saveAssignments = async () => {
    if (!instanceId) return;
    setSavingAssignments(true);
    try {
      const payload = Object.entries(assignments).map(([userId, data]) => ({
        userId: Number(userId),
        clientRepRateId: data.clientRepRateId ? Number(data.clientRepRateId) : null,
        orderRepRateId: data.orderRepRateId ? Number(data.orderRepRateId) : null,
        clientOrderRepRateId: data.clientOrderRepRateId ? Number(data.clientOrderRepRateId) : null,
      }));
      await axios.put(
        `${SERVER}/instances/${instanceId}/commission-assignments`,
        { assignments: payload },
        headers
      );
      fetchAssignments(instanceId);
    } finally {
      setSavingAssignments(false);
    }
  };

  const saveExclusions = async () => {
    if (!instanceId) return;
    setSavingExclusions(true);
    try {
      await axios.put(
        `${SERVER}/instances/${instanceId}/commission-exclusions`,
        exclusions,
        headers
      );
    } finally {
      setSavingExclusions(false);
    }
  };

  const saveMaximum = async () => {
    if (!instanceId) return;
    setSavingMaximum(true);
    try {
      await axios.put(
        `${SERVER}/instances/${instanceId}/commission-maximum`,
        {
          maxCommissionPercent:
            maxCommissionPercent === "" ? null : Number(maxCommissionPercent),
        },
        headers
      );
      fetchMaximum(instanceId);
    } finally {
      setSavingMaximum(false);
    }
  };

  const rateOptions = useMemo(() => rates || [], [rates]);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Tabs value={tab} onChange={(_e, value) => setTab(value)} variant="scrollable">
        <Tab label="Base Rates" />
        <Tab label="Rep Assignments" />
        <Tab label="Excludes" />
        <Tab label="Maximum Commission" />
        <Tab label="Margin Tables" />
        <Tab label="Client Rates" />
        <Tab label="Sales Goals" />
      </Tabs>

      {tab === 0 && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Commission Rates
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Define base commission rates for this instance.
              </Typography>
            </Box>
            <Button variant="contained" onClick={openCreateRate}>
              Add Rate
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rates || []).map((rate) => (
                <TableRow key={rate.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{rate.name}</TableCell>
                  <TableCell>{Number(rate.rate).toFixed(2)}%</TableCell>
                  <TableCell>{rate.rateType}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditRate(rate)} size="small">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => deleteRate(rate)} size="small">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(rates || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                    No commission rates yet. Add your first one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Dialog open={ratesOpen} onClose={() => setRatesOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {rateDraft.id ? "Edit Commission Rate" : "Create Commission Rate"}
            </DialogTitle>
            <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
              <TextField
                label="Name"
                value={rateDraft.name}
                onChange={(e) => setRateDraft((prev) => ({ ...prev, name: e.target.value }))}
              />
              <TextField
                label="Rate (%)"
                type="number"
                value={rateDraft.rate}
                onChange={(e) => setRateDraft((prev) => ({ ...prev, rate: e.target.value }))}
                inputProps={{ step: "0.01" }}
              />
              <TextField
                label="Rate Type"
                select
                value={rateDraft.rateType}
                onChange={(e) => setRateDraft((prev) => ({ ...prev, rateType: e.target.value }))}
              >
                {rateTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setRatesOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={saveRate}
                disabled={!rateDraft.name.trim() || !Number.isFinite(Number(rateDraft.rate))}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Rep Assignments
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Assign your rep&apos;s commission based on their role. (Rates may be applied only to order-rep, client-rep, operations-rep and administrator role.)
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              These rates map to the &quot;Add Commission&quot; modal on a project under "Commissions" - when finance selects which rep
              is being commissioned (order rep / client rep / both), the system uses the matching rate you set
              in the table below.
            </Typography>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rep</TableCell>
                <TableCell>Client Rep Only</TableCell>
                <TableCell>Order Rep Only</TableCell>
                <TableCell>Client + Order Rep</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(users || [])
                .filter((u) =>
                  ["order-rep", "client-rep", "operations-rep", "administrator", "super-administrator"].includes(u.role)
                )
                .map((user) => {
                  const entry = assignments[user.id] || {};
                  const name =
                    [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;

                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: "grid", lineHeight: 1.15 }}>
                          <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.7, textTransform: "capitalize" }}>
                            {user.role}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <TextField
                          select
                          value={entry.clientRepRateId ?? ""}
                          onChange={(e) =>
                            updateAssignment(user.id, "clientRepRateId", e.target.value)
                          }
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="">None</MenuItem>
                          {rateOptions.map((rate) => (
                            <MenuItem key={rate.id} value={rate.id}>
                              {rate.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>

                      <TableCell>
                        <TextField
                          select
                          value={entry.orderRepRateId ?? ""}
                          onChange={(e) =>
                            updateAssignment(user.id, "orderRepRateId", e.target.value)
                          }
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="">None</MenuItem>
                          {rateOptions.map((rate) => (
                            <MenuItem key={rate.id} value={rate.id}>
                              {rate.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>

                      <TableCell>
                        <TextField
                          select
                          value={entry.clientOrderRepRateId ?? ""}
                          onChange={(e) =>
                            updateAssignment(user.id, "clientOrderRepRateId", e.target.value)
                          }
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="">None</MenuItem>
                          {rateOptions.map((rate) => (
                            <MenuItem key={rate.id} value={rate.id}>
                              {rate.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  );
                })}

              {(users || []).filter((u) =>
                ["order-rep", "client-rep", "operations-rep", "administrator", "super-administrator"].includes(u.role)
              ).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                    No eligible reps found for this instance.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={saveAssignments} disabled={savingAssignments}>
              {savingAssignments ? "Saving..." : "Save Assignments"}
            </Button>
          </Box>
        </Box>
      )}

      {tab === 2 && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Exclusions
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Choose whether to include each item in commission calculations.
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Freight commission</Typography>
              <RadioGroup
                row
                value={exclusions.includeFreightCommission ? "include" : "exclude"}
                onChange={(e) =>
                  setExclusions((prev) => ({
                    ...prev,
                    includeFreightCommission: e.target.value === "include",
                  }))
                }
              >
                <FormControlLabel value="include" control={<Radio />} label="Included" />
                <FormControlLabel value="exclude" control={<Radio />} label="Excluded" />
              </RadioGroup>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700 }}>Fulfillment commission</Typography>
              <RadioGroup
                row
                value={exclusions.includeFulfillmentCommission ? "include" : "exclude"}
                onChange={(e) =>
                  setExclusions((prev) => ({
                    ...prev,
                    includeFulfillmentCommission: e.target.value === "include",
                  }))
                }
              >
                <FormControlLabel value="include" control={<Radio />} label="Included" />
                <FormControlLabel value="exclude" control={<Radio />} label="Excluded" />
              </RadioGroup>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700 }}>Shipping commission</Typography>
              <RadioGroup
                row
                value={exclusions.includeShippingCommission ? "include" : "exclude"}
                onChange={(e) =>
                  setExclusions((prev) => ({
                    ...prev,
                    includeShippingCommission: e.target.value === "include",
                  }))
                }
              >
                <FormControlLabel value="include" control={<Radio />} label="Included" />
                <FormControlLabel value="exclude" control={<Radio />} label="Excluded" />
              </RadioGroup>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={saveExclusions} disabled={savingExclusions}>
              {savingExclusions ? "Saving..." : "Save Exclusions"}
            </Button>
          </Box>
        </Box>
      )}

      {tab === 3 && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Maximum Commission
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Set a maximum total commission percentage per project.
            </Typography>
          </Box>

          <TextField
            label="Maximum Commission (%)"
            type="number"
            value={maxCommissionPercent}
            onChange={(e) => setMaxCommissionPercent(e.target.value)}
            inputProps={{ step: "0.01" }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={saveMaximum} disabled={savingMaximum}>
              {savingMaximum ? "Saving..." : "Save Maximum"}
            </Button>
          </Box>
        </Box>
      )}

      {tab === 4 && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Margin Table
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Apply a multiplier based on project margin ranges.
              </Typography>
            </Box>
            <Button variant="contained" onClick={openCreateMargin}>
              Add Range
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Margin Min (%)</TableCell>
                <TableCell>Margin Max (%)</TableCell>
                <TableCell>Multiplier</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(marginRows || []).map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.minMargin}</TableCell>
                  <TableCell>{row.maxMargin ?? "—"}</TableCell>
                  <TableCell>{Number(row.multiplier).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditMargin(row)} size="small">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => deleteMargin(row)} size="small">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(marginRows || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                    No margin ranges yet. Add your first one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Dialog open={marginOpen} onClose={() => setMarginOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {marginDraft.id ? "Edit Margin Range" : "Create Margin Range"}
            </DialogTitle>
            <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
              <TextField
                label="Margin Min (%)"
                type="number"
                value={marginDraft.min}
                onChange={(e) => setMarginDraft((prev) => ({ ...prev, min: e.target.value }))}
              />
              <TextField
                label="Margin Max (%)"
                type="number"
                value={marginDraft.max}
                onChange={(e) => setMarginDraft((prev) => ({ ...prev, max: e.target.value }))}
                helperText="Leave blank for no upper limit."
              />
              <TextField
                label="Multiplier"
                type="number"
                value={marginDraft.multiplier}
                onChange={(e) =>
                  setMarginDraft((prev) => ({ ...prev, multiplier: e.target.value }))
                }
                inputProps={{ step: "0.01" }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setMarginOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={saveMargin}
                disabled={
                  !Number.isFinite(Number(marginDraft.min)) ||
                  !Number.isFinite(Number(marginDraft.multiplier)) ||
                  savingMargin
                }
              >
                {savingMargin ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {tab === 5 && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Client Rates
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Apply a multiplier to commissions for specific clients.
              </Typography>
            </Box>
            <Button variant="contained" onClick={openCreateClientRate}>
              Add Client Rate
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Multiplier</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(clientRates || []).map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                  <TableCell>{Number(row.multiplier).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditClientRate(row)} size="small">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => deleteClientRate(row)} size="small">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(clientRates || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                    No client rates yet. Add your first one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Dialog open={clientOpen} onClose={() => setClientOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {clientDraft.id ? "Edit Client Rate" : "Create Client Rate"}
            </DialogTitle>
            <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
              <TextField
                label="Label"
                value={clientDraft.label}
                onChange={(e) => setClientDraft((prev) => ({ ...prev, label: e.target.value }))}
              />
              <TextField
                label="Multiplier"
                type="number"
                value={clientDraft.multiplier}
                onChange={(e) =>
                  setClientDraft((prev) => ({ ...prev, multiplier: e.target.value }))
                }
                inputProps={{ step: "0.01" }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setClientOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={saveClientRate}
                disabled={!clientDraft.label.trim() || !Number.isFinite(Number(clientDraft.multiplier))}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {tab === 6 && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Sales Goals
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Adjust commissions based on goal attainment.
              </Typography>
            </Box>
            <Button variant="contained" onClick={openCreateGoal}>
              Add Goal Range
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>% of Goal Min</TableCell>
                <TableCell>% of Goal Max</TableCell>
                <TableCell>Multiplier</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(goalRows || []).map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.minPercent}</TableCell>
                  <TableCell>{row.maxPercent ?? "—"}</TableCell>
                  <TableCell>{Number(row.multiplier).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEditGoal(row)} size="small">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => deleteGoal(row)} size="small">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(goalRows || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                    No sales goal ranges yet. Add your first one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Dialog open={goalOpen} onClose={() => setGoalOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {goalDraft.id ? "Edit Goal Range" : "Create Goal Range"}
            </DialogTitle>
            <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
              <TextField
                label="% of Goal Min"
                type="number"
                value={goalDraft.min}
                onChange={(e) => setGoalDraft((prev) => ({ ...prev, min: e.target.value }))}
              />
              <TextField
                label="% of Goal Max"
                type="number"
                value={goalDraft.max}
                onChange={(e) => setGoalDraft((prev) => ({ ...prev, max: e.target.value }))}
                helperText="Leave blank for no upper limit."
              />
              <TextField
                label="Multiplier"
                type="number"
                value={goalDraft.multiplier}
                onChange={(e) =>
                  setGoalDraft((prev) => ({ ...prev, multiplier: e.target.value }))
                }
                inputProps={{ step: "0.01" }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setGoalOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={saveGoal}
                disabled={
                  !Number.isFinite(Number(goalDraft.min)) ||
                  !Number.isFinite(Number(goalDraft.multiplier)) ||
                  savingGoals
                }
              >
                {savingGoals ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
}
